import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Modal,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import CustomButton from "components/common/CustomButton/CustomButton";
import { customStylesSelector } from "store/selectors/selectors";
import { useT } from "translation";
import { formatCurrency } from "utils/utils";

interface Package {
  code: number;
  description: string;
  paymentMethods: PaymentMethod[];
}

interface PaymentMethod {
  code: number;
  description: string;
  installments: Installment[];
}

interface Installment {
  quantity: number;
  value: number;
}

interface InstallmentSelectionModalProps {
  open: boolean;
  onConfirm: (selectedPackage: Package, selectedInstallment: number, selectedPaymentMethod: number) => void;
  packages: Package[];
  loading?: boolean;
}

const InstallmentSelectionModal: React.FC<InstallmentSelectionModalProps> = ({
  open,
  onConfirm,
  packages,
  loading = false,
}) => {
  const { t } = useT();
  const theme = useTheme();
  const customStyles = useSelector(customStylesSelector);
  
  const [selectedInstallments, setSelectedInstallments] = useState<number>(1);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number>(1);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);

  // Initialize with first package and payment method
  useEffect(() => {
    if (packages && packages.length > 0 && !selectedPackage) {
      setSelectedPackage(packages[0]);
      if (packages[0].paymentMethods && packages[0].paymentMethods.length > 0) {
        setSelectedPaymentMethod(packages[0].paymentMethods[0].code);
      }
    }
  }, [packages]);

  const handleInstallmentChange = (event: SelectChangeEvent<number>) => {
    setSelectedInstallments(event.target.value as number);
  };

  const handlePaymentMethodChange = (event: SelectChangeEvent<number>) => {
    setSelectedPaymentMethod(event.target.value as number);
  };

  const handleConfirm = () => {
    if (selectedPackage) {
      onConfirm(selectedPackage, selectedInstallments, selectedPaymentMethod);
    }
  };

  const getCurrentPaymentMethod = (pkg: Package): PaymentMethod | undefined => {
    return pkg.paymentMethods.find((pm) => pm.code === selectedPaymentMethod);
  };

  const getCurrentInstallmentValue = (pkg: Package): number => {
    const paymentMethod = getCurrentPaymentMethod(pkg);
    if (!paymentMethod) return 0;
    
    const installment = paymentMethod.installments.find(
      (inst) => inst.quantity === selectedInstallments
    );
    return installment ? installment.value : 0;
  };

  const getAvailableInstallments = (): Installment[] => {
    if (!selectedPackage) return [];
    const paymentMethod = getCurrentPaymentMethod(selectedPackage);
    return paymentMethod ? paymentMethod.installments : [];
  };

  const getAvailablePaymentMethods = (): PaymentMethod[] => {
    if (!packages || packages.length === 0) return [];
    return packages[0].paymentMethods || [];
  };

  if (loading) {
    return (
      <Modal
        open={open}
        aria-labelledby="installment-selection-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      aria-labelledby="installment-selection-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: customStyles.primaryCard.backgroundColor,
          overflow: "auto",
          p: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontWeight: 600,
              color: theme.palette.primary.main,
              textAlign: "center",
            }}
          >
            {t("SELECT_YOUR_PLAN_AND_INSTALLMENTS") || "Selecione seu plano e parcelamento"}
          </Typography>

          {/* Payment Method Selector */}
          {/* <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
            <FormControl sx={{ minWidth: 300 }}>
              <Typography
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {t("PAYMENT_METHOD") || "Forma de Pagamento"}
              </Typography>
              <Select
                value={selectedPaymentMethod}
                onChange={handlePaymentMethodChange}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: customStyles.secondaryCard.backgroundColor,
                }}
              >
                {getAvailablePaymentMethods().map((pm) => (
                  <MenuItem key={pm.code} value={pm.code}>
                    {pm.description.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}

          {/* Installment Selector */}
          <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
            <FormControl sx={{ minWidth: 300 }}>
              <Typography
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {t("NUMBER_OF_INSTALLMENTS") || "Número de Parcelas"}
              </Typography>
              <Select
                value={selectedInstallments}
                onChange={handleInstallmentChange}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: customStyles.secondaryCard.backgroundColor,
                }}
              >
                  {getAvailableInstallments().map((inst) => (
                    <MenuItem key={inst.quantity} value={inst.quantity}>
                      {inst.quantity}x - R$ {inst.value.toFixed(2)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {/* Plan Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: packages.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr",
              },
              gap: 3,
              mb: 4,
            }}
          >
            {packages.map((pkg) => {
              const currentValue = getCurrentInstallmentValue(pkg);
              // Clean up package name to show only the first word
              const cleanPackageName = pkg.description.trim().split(/\s+/)[0];

              return (
                <Box
                  key={pkg.code}
                  // onClick={() => setSelectedPackage(pkg)}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    border: "2px solid transparent",
                    backgroundColor: customStyles.secondaryCard.backgroundColor,
                    cursor: "pointer",
                    // transition: "all 0.3s ease",
                    // "&:hover": {
                    //   borderColor: theme.palette.primary.main,
                    //   transform: "translateY(-4px)",
                    //   boxShadow: `0 8px 16px rgba(0,0,0,0.1)`,
                    // },
                    // ...(isSelected && {
                    //   boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
                    // }),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      mb: 2,
                      textAlign: "center",
                    }}
                  >
                    {cleanPackageName}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {selectedInstallments}x de
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                      }}
                    >
                      R$ {currentValue.toFixed(2)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: theme.palette.text.secondary,
                        mt: 1,
                      }}
                    >
                      Total: R$ {(currentValue * selectedInstallments).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Confirm Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CustomButton
              btnText={t("CONFIRM_SELECTION") || "Confirmar Seleção"}
              onClickCB={handleConfirm}
              disabled={!selectedPackage}
              sx={{
                minWidth: "300px",
                padding: "12px 24px",
                fontSize: "16px",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default InstallmentSelectionModal;

